import { useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';
import i18n from '@/config/i18n';
import type { SupportedLanguage } from '@/lib/languageUtils';

/** Languages that have URL prefixes (everything except English) */
export const LANG_PREFIXES = ['pl', 'de', 'es', 'fil', 'hi', 'id', 'pt-br', 'pt-pt', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl'] as const;
export type LangPrefix = (typeof LANG_PREFIXES)[number];

const LANG_SET = new Set<string>(LANG_PREFIXES);

/** Strip language prefix from a pathname, e.g. /pl/blog/foo → /blog/foo */
export function stripLangPrefix(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && LANG_SET.has(segments[0])) {
    return '/' + segments.slice(1).join('/') || '/';
  }
  return pathname;
}

/**
 * Paths that must never receive a lang prefix — app/protected routes and
 * EN-only programmatic content. Matches the skip list in HreflangTags so
 * lp() and hreflang stay aligned. A request to lp("/auth") from a French
 * page must return "/auth", not "/fr/auth" — the latter is a 404.
 */
const LANG_SKIP_PREFIXES = [
  '/auth',
  '/dashboard',
  '/cctv-design',
  '/checkout-success',
  '/api-docs',
  '/camera/',
  '/compare/sku/',
];

function isLangSkippablePath(path: string): boolean {
  if (!path.startsWith('/')) return false;
  // Strip query/hash so "/auth?mode=signup" still matches "/auth".
  const pathOnly = path.split(/[?#]/)[0];
  return LANG_SKIP_PREFIXES.some(
    p => pathOnly === p || pathOnly.startsWith(p.endsWith('/') ? p : p + '/'),
  );
}

/** Build a localized path: /pl/blog/foo for non-English, /blog/foo for English.
 *  Returns the path unchanged for protected/EN-only routes (auth, dashboard,
 *  admin, the app itself, programmatic camera pages, etc.) — these never
 *  have lang variants and prefixing them would 404. */
export function buildLangPath(basePath: string, lang: string): string {
  if (lang === 'en') return basePath;
  if (isLangSkippablePath(basePath)) return basePath;
  const clean = basePath.startsWith('/') ? basePath : `/${basePath}`;
  return `/${lang}${clean}`;
}

/**
 * Per-language cloned i18n instances. Each clone has its own independent
 * `language` field while sharing resources, so concurrent SSG renders cannot
 * race each other through the singleton. Same pattern as BlogPostWrapper —
 * fixes the bug where /pl/about rendered with DE content because the global
 * singleton was mid-flight on a sibling render.
 */
const i18nByLang = new Map<string, ReturnType<typeof i18n.cloneInstance>>();
function getI18nForLang(lang: string) {
  let inst = i18nByLang.get(lang);
  if (!inst) {
    inst = i18n.cloneInstance({ lng: lang });
    i18nByLang.set(lang, inst);
  }
  return inst;
}

/**
 * Wrapper component for language-prefixed routes (`/:lang/...`).
 *
 * SSG: reads the language from the URL pathname (since `:lang` literal-route
 * params are not bound in vite-react-ssg's static-paths mode) and wraps
 * children in a per-language cloned I18nextProvider. Globally-singleton i18n
 * stays untouched, eliminating cross-language SSG render races.
 *
 * Client: keeps the LanguageSelector navigation-on-change behaviour by syncing
 * the global useLanguage() hook with the URL after hydration.
 */
export default function LangWrapper({ children }: { children: React.ReactNode }) {
  const { lang: paramLang } = useParams<{ lang: string }>();
  const { currentLanguage, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const mountSyncDone = useRef(false);
  const skipNextRedirect = useRef(false);

  // Detect language from URL path. The /:lang/... routes are generated as
  // literal paths (e.g. "pl/about") with no dynamic :lang param, so
  // `paramLang` is undefined here — read the first segment instead.
  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const urlLang = firstSegment && LANG_SET.has(firstSegment) ? firstSegment : 'en';
  const effectiveLang = paramLang || urlLang;

  // Per-render cloned i18n instance, isolated from other concurrent SSG renders.
  const langI18n = useMemo(() => getI18nForLang(effectiveLang), [effectiveLang]);

  // Client: sync language FROM URL → global hook after hydration.
  // (LanguageSelector reads/writes the global i18n via useLanguage; we keep
  // that wiring alive so the selector still works on a non-LangWrapper page.)
  useEffect(() => {
    if (!effectiveLang || effectiveLang === 'en') return;
    if (effectiveLang !== currentLanguage) {
      skipNextRedirect.current = true;
      changeLanguage(effectiveLang as SupportedLanguage);
    }
    mountSyncDone.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On language change (user switches via LanguageSelector): navigate to correct URL
  useEffect(() => {
    if (!mountSyncDone.current) return;
    if (skipNextRedirect.current) {
      skipNextRedirect.current = false;
      return;
    }

    const basePath = stripLangPrefix(location.pathname);
    const targetPath = buildLangPath(basePath, currentLanguage);

    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  return <I18nextProvider i18n={langI18n}>{children}</I18nextProvider>;
}
